import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { CurrencyObject, DateFormatObject } from 'common/types';
import { CustomersTableRow } from './rows';
import moment from 'moment-timezone';
import { localFormat } from 'common/utils/dateUtils';
import React from 'react';
import { TFunction } from 'react-i18next';
import { getPricingString } from 'common/utils/common';

interface CustomersTableColDef extends GridColDef {
	field: keyof CustomersTableRow;
	renderCell?: (params: GridRenderCellParams<any, CustomersTableRow>) => React.ReactNode;
}

export const getCustomersTableColumns = (deps: {
	dateFormat: DateFormatObject;
	currency: CurrencyObject;
	t: TFunction;
}): GridColDef[] => {
	const { dateFormat, currency, t } = deps;

	const getFormattedDate = (date: string | undefined) => {
		if (!date) return '-';
		const momentDate = moment(date);

		return localFormat(momentDate, 'DD.MM.YYYY', dateFormat);
	};

	const columns: CustomersTableColDef[] = [
		{
			field: 'firstName',
			headerName: t('common:form.firstName', 'First name'),
			minWidth: 150,
			filterable: false,
			sortable: false,
		},
		{
			field: 'lastName',
			headerName: t('common:form.lastName', 'Last name'),
			minWidth: 150,
			filterable: false,
			sortable: false,
		},
		{
			field: 'email',
			headerName: t('common:form.email', 'Email'),
			minWidth: 150,
			filterable: false,
			sortable: false,
		},
		{
			field: 'phone',
			headerName: t('common:form.phone', 'Phone'),
			minWidth: 150,
			filterable: false,
			sortable: false,
		},
		{
			field: 'marketingConsent',
			headerName: t('customers.tableHeader.marketingConsent', 'Marketing consent'),
			minWidth: 200,
			sortable: false,
			getApplyQuickFilterFn: (value) => (params) => {
				return params.value === value.operatorValue;
			},
			filterOperators: [
				{
					value: t('common:actions.yes', 'Yes'),
					label: t('common:actions.yes', 'Yes'),
					getApplyFilterFn: (value) => (params) => {
						return params.value === value.operatorValue;
					},
				},
				{
					value: t('common:actions.no', 'No'),
					label: t('common:actions.no', 'No'),
					getApplyFilterFn: (value) => (params) => {
						return params.value === value.operatorValue;
					},
				},
			],
		},
		{
			field: 'total',
			headerName: t('customers.tableHeader.total', {
				currency: currency.displayAs,
				defaultValue: 'Total ({{currency}})',
			}),
			minWidth: 150,
			renderCell: ({ value }) => getPricingString((value as number) * 100),
			sortable: false,
			type: 'number',
		},
		{
			field: 'purchaseDate',
			headerName: t('customers.tableHeader.lastPurchaseDate', 'Last purchase date'),
			valueFormatter: ({ value }) => getFormattedDate(value as string),
			minWidth: 200,
			type: 'date',
			filterable: false,
			sortable: false,
		},
		{
			field: 'products',
			headerName: t('products.products', 'Products'),
			minWidth: 200,
			sortable: false,
		},
		{
			field: 'channels',
			headerName: t('shop.channels', 'Channels'),
			minWidth: 150,
			sortable: false,
		},
		{
			field: 'categories',
			headerName: t('customers.tableHeader.categories', 'Categories'),
			minWidth: 200,
			sortable: false,
		},
		{
			field: 'totalOrders',
			headerName: t('customers.tableHeader.totalOrders', 'Total orders'),
			minWidth: 200,
			sortable: false,
			type: 'number',
		},
	];

	return columns;
};
